import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { FileDropZone } from 'ui/FileDrop/FileDropZone';

import styles from './CameraCaptureModal.module.scss';

type FacingMode = 'user' | 'environment';

type VisualFilterId = 'none' | 'warm' | 'cool' | 'bw' | 'contrast';

const VISUAL_FILTERS: { id: VisualFilterId; label: string; css: string }[] = [
  { id: 'none', label: 'Без фильтра', css: 'none' },
  { id: 'warm', label: 'Теплый', css: 'sepia(0.28) saturate(1.15) brightness(1.03)' },
  { id: 'cool', label: 'Холодный', css: 'saturate(0.85) hue-rotate(195deg) brightness(1.02)' },
  { id: 'bw', label: 'Ч/Б', css: 'grayscale(1) contrast(1.05)' },
  { id: 'contrast', label: 'Контраст', css: 'contrast(1.25) saturate(1.1)' },
];

export type CameraCaptureVariant = 'mobile' | 'desktop';

type CameraCaptureModalProps = {
  /** mobile: start camera immediately; desktop: chooser first (webcam / file). */
  variant?: CameraCaptureVariant;
  onCancel: () => void;
  onCapture: (file: File) => void;
  /** Native capture input (mobile system camera). */
  onFallback: (facing: FacingMode) => void;
  /** Desktop file picker without requesting camera permission. */
  onPickFile?: () => void;
};

export const CameraCaptureModal: React.FC<CameraCaptureModalProps> = ({
  variant = 'mobile',
  onCancel,
  onCapture,
  onFallback,
  onPickFile,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [phase, setPhase] = useState<'chooser' | 'camera'>(
    variant === 'desktop' ? 'chooser' : 'camera',
  );
  const [facing, setFacing] = useState<FacingMode>('user');
  const [filterId, setFilterId] = useState<VisualFilterId>('none');
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);

  const activeFilterCss =
    VISUAL_FILTERS.find((item) => item.id === filterId)?.css ?? 'none';

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  const startCamera = useCallback(
    async (mode: FacingMode) => {
      setError(null);
      setReady(false);
      stopStream();
      if (!navigator.mediaDevices?.getUserMedia) {
        setError('Камера недоступна в этом браузере. Выберите файл или системную камеру.');
        return;
      }
      if (window.isSecureContext === false) {
        setError('Камера работает только по HTTPS. Откройте сайт по защищенному адресу.');
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            facingMode: { ideal: mode },
            width: { ideal: 1280 },
            height: { ideal: 1720 },
          },
        });
        streamRef.current = stream;
        const video = videoRef.current;
        if (video) {
          video.srcObject = stream;
          await video.play();
          setReady(true);
        }
      } catch (err) {
        const name = err instanceof DOMException ? err.name : '';
        if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
          setError('Нет доступа к камере. Разрешите доступ или выберите файл.');
        } else if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
          setError('Камера не найдена на устройстве.');
        } else {
          setError('Не удалось открыть камеру. Попробуйте выбрать файл.');
        }
      }
    },
    [stopStream],
  );

  useEffect(() => {
    if (phase !== 'camera') {
      stopStream();
      return;
    }
    void startCamera(facing);
    return () => {
      stopStream();
    };
  }, [phase, facing, startCamera, stopStream]);

  const handleCapture = async () => {
    const video = videoRef.current;
    if (!video || !ready) return;
    setBusy(true);
    try {
      const width = video.videoWidth || 720;
      const height = video.videoHeight || 960;
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Не удалось сделать снимок');
      if (activeFilterCss !== 'none') {
        ctx.filter = activeFilterCss;
      }
      ctx.drawImage(video, 0, 0, width, height);
      ctx.filter = 'none';
      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (result) => (result ? resolve(result) : reject(new Error('Не удалось сохранить снимок'))),
          'image/jpeg',
          0.92,
        );
      });
      stopStream();
      onCapture(new File([blob], `camera-${Date.now()}.jpg`, { type: 'image/jpeg' }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось сделать снимок');
    } finally {
      setBusy(false);
    }
  };

  const toggleFacing = () => {
    setFacing((prev) => (prev === 'user' ? 'environment' : 'user'));
  };

  if (phase === 'chooser') {
    return (
      <div className={styles.overlay} role='dialog' aria-modal='true' aria-label='Добавить фото'>
        <FileDropZone
          className={styles.modal}
          accept="image/jpeg,image/png,image/heic,image/heif,.jpg,.jpeg,.png,.heic,.heif"
          hint="Отпустите фото, чтобы загрузить"
          onFiles={(files) => {
            const file = files[0];
            if (file) onCapture(file);
          }}
        >
          <h3 className={styles.title}>Добавить фото</h3>
          <p className={styles.chooserHint}>
            На компьютере удобнее выбрать файл. Веб-камеру можно включить, если она подключена.
          </p>
          <div className={styles.chooserActions}>
            <button
              type='button'
              className={styles.primary}
              onClick={() => {
                onPickFile?.();
              }}
            >
              Выбрать файл
            </button>
            <button
              type='button'
              className={styles.secondary}
              onClick={() => {
                setPhase('camera');
              }}
            >
              Веб-камера
            </button>
            <button type='button' className={styles.secondary} onClick={onCancel}>
              Отмена
            </button>
          </div>
        </FileDropZone>
      </div>
    );
  }

  return (
    <div className={styles.overlay} role='dialog' aria-modal='true' aria-label='Съемка фото'>
      <FileDropZone
        className={styles.modal}
        accept="image/jpeg,image/png,image/heic,image/heif,.jpg,.jpeg,.png,.heic,.heif"
        hint="Отпустите фото, чтобы загрузить"
        onFiles={(files) => {
          const file = files[0];
          if (file) onCapture(file);
        }}
      >
        <h3 className={styles.title}>Сделать фото</h3>
        <div className={styles.videoWrap}>
          <video
            ref={videoRef}
            className={styles.video}
            style={{ filter: activeFilterCss }}
            playsInline
            muted
            autoPlay
          />
          {!ready && !error ? <div className={styles.loading}>Подключение камеры...</div> : null}
        </div>

        <div className={styles.filters} role='group' aria-label='Фильтры'>
          {VISUAL_FILTERS.map((item) => {
            const active = filterId === item.id;
            return (
              <button
                key={item.id}
                type='button'
                className={`${styles.filterChip} ${active ? styles.filterChipActive : ''}`}
                aria-pressed={active}
                disabled={busy}
                onClick={() => setFilterId(item.id)}
              >
                {item.label}
              </button>
            );
          })}
        </div>

        {error ? <p className={styles.error}>{error}</p> : null}
        <div className={styles.actions}>
          <button
            type='button'
            className={styles.secondary}
            onClick={() => {
              stopStream();
              if (variant === 'desktop') {
                setPhase('chooser');
                setReady(false);
                setError(null);
              } else {
                onCancel();
              }
            }}
            disabled={busy}
          >
            {variant === 'desktop' ? 'Назад' : 'Отмена'}
          </button>
          <button type='button' className={styles.secondary} onClick={toggleFacing} disabled={busy}>
            {facing === 'user' ? 'Основная камера' : 'Фронтальная'}
          </button>
          {variant === 'mobile' ? (
            <button
              type='button'
              className={styles.secondary}
              onClick={() => {
                stopStream();
                onFallback(facing);
              }}
              disabled={busy}
            >
              Системная камера
            </button>
          ) : (
            <button
              type='button'
              className={styles.secondary}
              onClick={() => {
                stopStream();
                onPickFile?.();
              }}
              disabled={busy}
            >
              Выбрать файл
            </button>
          )}
          <button
            type='button'
            className={styles.primary}
            onClick={() => void handleCapture()}
            disabled={busy || !ready}
          >
            {busy ? 'Снимок...' : 'Сфотографировать'}
          </button>
        </div>
      </FileDropZone>
    </div>
  );
};
